import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { peptideProducts, bundleProducts } from '../../mocks/products';

export default function PeptidesPage() {
  const [activeTab, setActiveTab] = useState<'peptides' | 'bundles'>('peptides');
  const [expandedBundle, setExpandedBundle] = useState<number | null>(null);
  const [cart, setCart] = useState<Array<{ id: number; quantity: number }>>([]);
  const [warningOpen, setWarningOpen] = useState(false);

  // Safeguard addToCart against invalid product ids
  const addToCart = (productId: number) => {
    if (typeof productId !== 'number' || isNaN(productId)) {
      console.error('Invalid product id passed to addToCart:', productId);
      return;
    }
    setCart((prev) => {
      const existing = prev.find((item) => item.id === productId);
      if (existing) {
        return prev.map((item) =>
          item.id === productId ? { ...item, quantity: item.quantity + 1 } : item,
        );
      }
      return [...prev, { id: productId, quantity: 1 }];
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Warning Disclaimer Banner */}
      <div className="fixed top-20 left-0 right-0 z-45 flex justify-center pointer-events-none" style={{ zIndex: 45 }}>
        <div className="pointer-events-auto">
          {!warningOpen ? (
            <button
              onClick={() => setWarningOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white text-xs font-bold rounded-b-lg shadow-lg hover:bg-amber-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-error-warning-line text-base"></i>
              IMPORTANT DISCLAIMER
              <i className="ri-arrow-down-s-line text-base"></i>
            </button>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-amber-50 border border-amber-300 rounded-b-xl shadow-xl max-w-2xl"
            >
              <div className="px-6 py-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-lg flex-shrink-0 mt-0.5">
                    <i className="ri-error-warning-line text-xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-amber-900 text-sm mb-2">Research Use Only — Not for Human Consumption</h3>
                    <p className="text-xs text-amber-800 leading-relaxed mb-2">
                      All peptide products sold on this website are intended <strong>strictly for laboratory and research purposes only</strong>. These products are <strong>not intended for human consumption</strong>, veterinary use, or any therapeutic application.
                    </p>
                    <p className="text-xs text-amber-800 leading-relaxed">
                      By purchasing, you acknowledge that these products will be used solely for in‑vitro research and that you assume full responsibility for compliance with all applicable local, state, and federal regulations.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => setWarningOpen(false)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 text-white text-xs font-semibold rounded-lg hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    I Understand
                    <i className="ri-arrow-up-s-line text-sm"></i>
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/c6f1e0652624cfaa46575fdd8d3eccd4.jpg"
            alt="Premium peptide laboratory"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-display font-bold text-5xl md:text-6xl text-white mb-6">
              Premium <span className="text-electric-blue">Peptides</span>
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
              Certified, pharmaceutical‑grade peptides for recovery, performance, and wellness optimization
            </p>
            <div className="flex justify-center gap-4">
              <Link
                to="/booking"
                className="px-8 py-3 bg-electric-blue text-white rounded-lg text-sm font-semibold hover:bg-blue-500 transition-all whitespace-nowrap cursor-pointer"
              >
                Book Peptide Consultation
              </Link>
              <Link
                to="/shop"
                className="px-8 py-3 bg-white/10 border border-white/30 text-white rounded-lg text-sm font-semibold hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
              >
                Shop Everything
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <button
              onClick={() => setActiveTab('peptides')}
              className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                activeTab === 'peptides'
                  ? 'bg-electric-blue text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className="ri-flask-line"></i>
              Individual Peptides
            </button>
            <button
              onClick={() => setActiveTab('bundles')}
              className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                activeTab === 'bundles'
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className="ri-gift-line"></i>
              Bundles &amp; Starter Kits
            </button>
          </div>
        </div>
      </section>

      {activeTab === 'peptides' && (
        <section className="py-20 bg-gradient-to-br from-gray-50 to-baby-blue/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
              {peptideProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group"
                >
                  <div className="relative h-80 bg-gradient-to-br from-baby-blue/20 to-white overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      title={`${product.name} - Mixtape Curated certified peptide`}
                      className="w-full h-full object-contain p-8 group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 px-3 py-1 bg-electric-blue text-white text-xs font-semibold rounded-full">
                      Peptide
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display font-bold text-xl text-navy mb-2">{product.name}</h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{product.description}</p>
                    <div className="mb-4 flex flex-wrap gap-2">
                      {product.benefits.map((benefit, idx) => (
                        <span key={idx} className="px-2 py-1 bg-baby-blue/30 text-navy text-xs rounded-full">
                          {benefit}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-navy">${product.price}</span>
                      <button
                        onClick={() => addToCart(product.id)}
                        className="px-6 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {activeTab === 'bundles' && (
        <section className="py-20 bg-gradient-to-br from-gray-50 to-baby-blue/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
              {bundleProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group relative border border-amber-200/60"
                >
                  <div className="absolute top-4 left-4 z-10 flex flex-col gap-1.5">
                    <span className="px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full whitespace-nowrap">
                      SAVE ${product.savings.toFixed(2)}
                    </span>
                    <span className="px-3 py-1 bg-navy text-white text-xs font-semibold rounded-full whitespace-nowrap">
                      Bundle
                    </span>
                  </div>
                  <div className="relative h-72 bg-gradient-to-br from-amber-50/60 to-orange-50/40 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      title={`${product.name} - Mixtape Curated peptide bundle`}
                      className="w-full h-full object-contain p-6 group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-display font-bold text-xl text-navy mb-2">{product.name}</h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{product.description}</p>
                    <div className="mb-4 flex flex-wrap gap-2">
                      {product.benefits.map((benefit, idx) => (
                        <span key={idx} className="px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full font-medium">
                          {benefit}
                        </span>
                      ))}
                    </div>
                    <div className="mb-4">
                      <button
                        onClick={() =>
                          setExpandedBundle(expandedBundle === product.id ? null : product.id)
                        }
                        className="flex items-center gap-1.5 text-xs font-semibold text-navy hover:text-electric-blue transition-colors cursor-pointer"
                      >
                        <i
                          className={`ri-arrow-${expandedBundle === product.id ? 'up' : 'down'}-s-line text-base`}
                        ></i>
                        {expandedBundle === product.id ? 'Hide' : 'Show'} what&apos;s included ({product.includes.length} items)
                      </button>
                      {expandedBundle === product.id && (
                        <motion.ul
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="mt-2 space-y-1.5 pl-1"
                        >
                          {product.includes.map((item, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-xs text-gray-600">
                              <i className="ri-check-line text-green-500 text-sm"></i>
                              {item}
                            </li>
                          ))}
                        </motion.ul>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-400 line-through">
                          ${product.originalPrice.toFixed(2)}
                        </span>
                        <span className="text-2xl font-bold text-navy">${product.price}</span>
                      </div>
                      <button
                        onClick={() => addToCart(product.id)}
                        className="px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg text-sm font-medium hover:from-amber-600 hover:to-orange-600 transition-all whitespace-nowrap cursor-pointer"
                      >
                        Add Bundle
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display font-bold text-4xl text-navy mb-4">Why Our Peptides?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Every vial is third‑party tested and certified for purity and potency
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'Third-Party Tested',
                desc: 'Every batch independently verified for 98%+ purity',
              },
              {
                icon: 'ri-truck-line',
                title: 'Fast & Discreet Shipping',
                desc: 'Temperature-controlled packaging shipped within 24 hours',
              },
              {
                icon: 'ri-customer-service-line',
                title: 'Expert Support',
                desc: 'Free consultation with every first order to guide your protocol',
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-baby-blue/20 to-white p-8 rounded-2xl border border-baby-blue/30 text-center"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-electric-blue rounded-xl mx-auto mb-4">
                  <i className={`${item.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="font-display font-bold text-lg text-navy mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {cart.length > 0 && (
        <div className="fixed bottom-8 right-8 z-50">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="bg-navy text-white px-6 py-4 rounded-full shadow-2xl flex items-center gap-3 cursor-pointer hover:bg-navy-dark transition-colors"
          >
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-shopping-cart-line text-2xl"></i>
            </div>
            <div>
              <div className="text-sm font-semibold">
                {cart.reduce((sum, item) => sum + item.quantity, 0)} items
              </div>
              <div className="text-xs text-white/70">View Cart</div>
            </div>
          </motion.div>
        </div>
      )}

      <Footer />
    </div>
  );
}
